import React from 'react';
import { ChartNoAxesCombined, ShieldAlert, SearchCheck, Bug, FileChartColumnIncreasing } from 'lucide-react';
import UpgradePage from 'src/shared/templates/upgrade-page/UpgradePage';
import useProjectBreadcrumb from 'src/shared/hooks/useProjectBreadcrumb';
import useProject from 'src/shared/hooks/useProject';
import analyticsTracking from 'src/services/analytics-service';
import ecologyHubUpgradeHero from 'src/assets/images/ecology-hub/upgrade/ecology-hub-upgrade-hero.webp';

/** Ecology Hub's own HubSpot form (portal/region match the shared default). */
const ECOLOGY_HUB_HUBSPOT_FORM_ID = '9eb1f7b2-d543-4606-ae2b-5037bcd19a9c';

const FEATURES = [
    {
        icon: ChartNoAxesCombined,
        title: 'Identify Significant Change',
        body: 'Move beyond simple observation to scientific proof by identifying statistically significant differences between your sample groups.',
    },
    {
        icon: ShieldAlert,
        title: 'Benchmark Community Health',
        body: 'Visualise how similar your sample groups are based on the species detected, allowing you to track how biological communities shift over time following restoration or land-use changes.',
    },
    {
        icon: SearchCheck,
        title: 'Communicate Taxonomic Complexity',
        body: 'Leverage automated heat trees to show species observations across all taxonomic ranks, making complex biological relationships easy for stakeholders and boards to grasp.',
    },
    {
        icon: Bug,
        title: 'Automated Trend Analysis',
        body: 'View both species and habitat metrics as a time series to provide clear, auditable evidence of progress toward biodiversity goals.',
    },
    {
        icon: FileChartColumnIncreasing,
        title: 'Deep-Dive Sample Comparison',
        body: 'Compare specific metric values across individual samples to pinpoint site-level outliers and optimise resource allocation across your portfolio.',
    },
];

const FreemiumEcologyHub = () => {
    const breadcrumbs = useProjectBreadcrumb('Ecology Hub');
    const { currentProjectDetails } = useProject();

    const handleBookCallClick = () =>
        analyticsTracking.trackEvent({
            action: 'upgrade_subscription_click_from_ecology_hub',
            category: 'upgrade-subscription-link',
            project: currentProjectDetails?.projectName,
        });

    return (
        <UpgradePage
            testId='ecology-hub-upgrade-page'
            breadcrumbs={breadcrumbs}
            heroImage={ecologyHubUpgradeHero}
            badgeLabel='Upgrade to the Ecology Hub'
            title='Master Your Ecological Data'
            description='Stop "data wrangling" and start analysing. The Ecology Hub transforms complex eDNA and habitat results into high-impact comparisons that provide the scientific rigour your nature strategy demands.'
            features={FEATURES}
            ctaTitle='Ready to unlock your full analytical potential?'
            ctaDescription="Upgrade your subscription today to compare sites, track community shifts, and evidence your impact with the world's most advanced eDNA intelligence platform."
            hubSpotForm={{ formId: ECOLOGY_HUB_HUBSPOT_FORM_ID }}
            onBookCallClick={handleBookCallClick}
        />
    );
};

export default FreemiumEcologyHub;
